import { http } from '@/plugins/http'
import Axios from 'axios'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_ADDRESSES,
  RECEIVING_ADDRESSES,
  RECEIVE_ADDRESS_METADATA,
  RECEIVE_ADDRESS_FAILURE,
  SAVING_ADDRESS,
  SAVING_ADDRESS_FAILURE,
  UPDATING_ADDRESS,
  UPDATING_ADDRESS_FAILURE,
  DELETE_ADDRESS,
  DELETING_ADDRESS,
  DELETING_ADDRESS_FAILURE,
  INCREMENT_ADDRESS_COUNT,
  CLEAR_ADDRESS_STATE,
  RESET_ADDRESS_META,
  CLEAR_STATE
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import {
  receiveData,
  deleteData,
  increment,
  receiveMetadata,
  clearLocalState,
  resetMetadata,
  clearState
} from '@/store/modules/helpers/mutationHelpers'

const addressSchema = new schema.Entity('addresses')
const addressListSchema = new schema.Array(addressSchema)

const state = {
  data: {},
  cache: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  saving: false,
  saveFailure: null,
  receivingData: false,
  receiveDataFailure: false,
  updating: null,
  updateFailure: null,
  deleting: false,
  deleteFailure: null
}

const actions = {
  async fetchAddressesWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_ADDRESSES, true)
    return http.get(buildUrlWithQuery('addresses', query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(async (r) => {
        this.commit(RECEIVING_ADDRESSES, false)
        this.commit(RESET_ADDRESS_META)
        if (r.hasOwnProperty('results')) {
          let { results, next, previous, count } = r
          let cityRes = await Axios.get(`${instanceUrl}cities?page_size=${results.length}&id__in=${[...new Set(results.map(res => res.city))].join()}`, { headers })
          const cityMap = new Map(cityRes.data.results.map(city => [city.id, city.text]))

          // Update the "city" value in the first array
          const updatedArray = results.map(item => {
            const cityName = cityMap.get(item.city)
            item.city = cityName || ''
            return item
          })
          results = updatedArray
          const { entities, result } = normalize(results, addressListSchema)
          if (!result || !Array.isArray(result)) return this.commit(RECEIVE_ADDRESS_FAILURE, 'Error normalizing response')
          this.commit(RECEIVE_ADDRESSES, { data: entities.addresses })
          this.commit(RECEIVE_ADDRESS_METADATA, { next, previous, count, result: result.length })
          return { query, count, entities }
        }
      })
      .catch(e => {
        this.commit(RECEIVING_ADDRESSES, false)
        this.commit(RECEIVE_ADDRESS_FAILURE, handleXHRError(e))
      })
  },
  saveAddress (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    if (payload) {
      this.commit(SAVING_ADDRESS, true)
      this.commit(SAVING_ADDRESS_FAILURE, null)
      return http.post(buildUrl('addresses', null, instanceUrl), payload, { headers })
        .then(checkStatus)
        .then(parseJSON)
        .then(r => {
          this.commit(SAVING_ADDRESS, false)
          if (!r) return this.commit(SAVING_ADDRESS_FAILURE, 'Error saving address')
          const { entities, result } = normalize(r, addressSchema)
          if (!result) return this.commit(RECEIVE_ADDRESS_FAILURE, 'Error normalizing response')
          this.commit(RECEIVE_ADDRESSES, { data: entities.addresses })
          this.commit(INCREMENT_ADDRESS_COUNT, 1)
          return r
        })
        .catch(e => {
          this.commit(SAVING_ADDRESS, false)
          this.commit(SAVING_ADDRESS_FAILURE, handleXHRError(e))
        })
    } else {
      this.commit(SAVING_ADDRESS, false)
      this.commit(SAVING_ADDRESS_FAILURE, 'Error saving addresses')
    }
  }
}

const getters = {
  getAddresses: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getAddressMetadata: (state) => {
    const { shown, count } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown }
  },
  getAddressById: (state) => (id) => {
    if (state.data && state.data[id]) {
      return state.data[id]
    } else if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getAddressFetchStatus: (state) => {
    return state.receivingData
  },
  getAddressSaveStatus: (state) => {
    return state.saving
  },
  getAddressSaveFailure: (state) => {
    return state.saveFailure
  },
  getAddressUpdateStatus: (state) => {
    return state.updating
  },
  getAddressUpdateFailure: (state) => {
    return state.updateFailure
  },
  getAddressDeleteStatus: (state) => {
    return state.deleting
  },
  getAddressDeleteFailure: (state) => {
    return state.deleteFailure
  }
}

const mutations = {
  [RECEIVE_ADDRESSES] (state, results) {
    receiveData(state, results)
  },
  [RECEIVING_ADDRESSES] (state, payload) {
    state.receivingData = payload
  },
  [RECEIVE_ADDRESS_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [SAVING_ADDRESS] (state, payload) {
    state.saving = payload
  },
  [SAVING_ADDRESS_FAILURE] (state, payload) {
    state.saveFailure = payload
  },
  [UPDATING_ADDRESS] (state, payload) {
    state.updating = payload
  },
  [UPDATING_ADDRESS_FAILURE] (state, payload) {
    const { id, err } = payload
    state.updateFailure = { id, err }
  },
  [DELETE_ADDRESS] (state, payload) {
    deleteData(state, payload)
  },
  [DELETING_ADDRESS] (state, payload) {
    state.deleting = payload
  },
  [DELETING_ADDRESS_FAILURE] (state, payload) {
    const { id, err } = payload
    state.deleteFailure = { id, err }
  },
  [INCREMENT_ADDRESS_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_ADDRESS_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [CLEAR_ADDRESS_STATE] (state, payload) {
    clearLocalState(state, payload)
  },
  [RESET_ADDRESS_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}
